import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup, FormControl, Validators } from '@angular/forms'
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ResourceEditComponent, ResourceFormService } from '@smartsoftware/reflex-core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
    AddressBook,
    AddressBookService,
    CommonData_Service,
    SystemConfig_service,
    SuccessCountry,
    SuccessCountry_Service,
    FieldLockService,
    validators,
    SuccessState,
    SuccessState_Service
} from 'legalreflex-lib';
import { AddressRestoreDialog } from '../restore/addressRestoreDialog';
import { BreadCrumbService } from '../../../components/breadcrumb-bar/breadcrumb-service';
import { filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
const PHONE_EXT_PATTERN = "^[0-9]*$";

@Component({
    templateUrl: './create.html',
    styleUrls: ['./create.scss']
})
export class AddressCreate extends ResourceEditComponent<AddressBook, AddressBookService> implements OnInit, AfterViewInit, OnDestroy {
    
    public privateCtrl = new FormControl(null);
    public stateCtrl = new FormControl(null);
    public copy = new FormGroup({});
    public corpId: string = '';
    public accountId: string = '';
    public synced = false;

    public states: SuccessState[] = [];

    private destroy$: Subject<void> = new Subject<void>();

    private delete$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private restore$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private route$: BehaviorSubject<any[] | undefined> = new BehaviorSubject<any[] | undefined>(undefined);
    private save$: Subject<Observable<void>> = new Subject<Observable<void>>();

    constructor(
        public locationService: CommonData_Service,
        protected auth: OidcSecurityService,
        protected entityService: AddressBookService,
        protected route: ActivatedRoute,
        protected dialog: MatDialog,
        protected snackbar: MatSnackBar,
        protected resourceFormService: ResourceFormService<AddressBook>,
        private breadCrumbService: BreadCrumbService,
        public sysConfig: SystemConfig_service,
        public countryService: SuccessCountry_Service,
        public fieldLockService: FieldLockService,
        protected router: Router,
        public stateService: SuccessState_Service
    ) {
        super(entityService, route, dialog, resourceFormService);

        this.entity = new AddressBook()
        this.entityForm = this.resourceFormService.from(new AddressBook())
        this.copy = this.resourceFormService.from(new AddressBook())

        this.auth.userData$.pipe(takeUntil(this.destroy$)).subscribe(({ userData, allUserData }) => {
			let response = userData;
            this.accountId = response.profile.uuid
            this.corpId = response.profile.corpId
        })
        
        this.countryService
            .getAllCountries()
            .pipe(takeUntil(this.destroy$))
            .subscribe((res: any) => {
                let r: SuccessCountry[] = res;
                if (r.length) {
                    r.sort((a, b) => {
                        return a.countryName > b.countryName ? 1 : 0;
                    });
                    r.unshift(
                        r.splice(
                            r.findIndex((ind) => ind.countryName == "United States of America"),
                            1
                        )[0]
                    );
                    this.locationService.countries = r.map((res: SuccessCountry) => {
                        return { label: res.countryName, value: res.countryAbbreviation };
                    });
                }
            });
    }

    ngOnInit() {
        super.ngOnInit()
        if(this.sysConfig.maintenanceRedirectCheck()){  
            window.localStorage.removeItem("loggedInUserPermissionNodes")
            this.auth.logoff()
        }

        this.entityForm
            .get("country")
            ?.valueChanges.pipe(
                startWith("US"),
                switchMap((countryAbbreviation: string) => this.stateService.getStatesFromCountryAbbreviation(countryAbbreviation)),
                map((states: SuccessState[]) => (this.states = states.sort((s1, s2) => s1.stateName.localeCompare(s2.stateName)))),
                takeUntil(this.destroy$)
            )
            .subscribe();

        this.setBreadCrumbs();

        this.entityStatus.pipe(takeUntil(this.destroy$)).subscribe((res) => { 
            if(res && res.uuid){
                if(!this.synced){
                    const syncLocks = res.syncLocks
                    if (syncLocks) {
                        this.fieldLockService.setLockedFields(syncLocks.split(','))
                    } else {
                        this.fieldLockService.setLockedFields([])
                    }
                    this.synced = true
                }

                this.trimEntity();

                if(this.usCatch(res.country.trim()))
                    res.country = 'US'
                else if(this.canadaCatch(res.country.trim()))
                    res.country = 'CA'

                this.entityForm = this.resourceFormService.from(res)
                this.copy = this.resourceFormService.from(res)

                if (res.country === 'US' || res.country === 'CA') {
                    this.entityForm.get('primaryPhoneNumber')?.addValidators([Validators.required, validators.USPhoneNumberValidator]);
                    this.entityForm.get('primaryFaxNumber')?.addValidators([validators.USPhoneNumberValidator]);
                } else {
                    this.entityForm.get('primaryPhoneNumber')?.addValidators([validators.requiredNotEmpty]);
                    this.entityForm.get('primaryFaxNumber')?.addValidators([validators.requiredNotEmpty]);
                }
                this.entityForm.get("phoneExt")?.addValidators([Validators.pattern(PHONE_EXT_PATTERN)])
                if (res.country === 'US')
                    this.entityForm.get('postalCode')?.addValidators([Validators.required, validators.USZipCodeValidator])
                else if (res.country === 'CA')
                    this.entityForm.get('postalCode')?.addValidators([Validators.required, validators.USCanadaPostalCodeValidator])
                else
                    this.entityForm.get('postalCode')?.addValidators([validators.requiredNotEmpty]);
                
                this.entityForm.get('primaryPhoneNumber')?.updateValueAndValidity()
                this.entityForm.get("phoneExt")?.updateValueAndValidity()
                this.entityForm.get('postalCode')?.updateValueAndValidity();
                this.entityForm.get('primaryFaxNumber')?.updateValueAndValidity()

                if(res.isPrivate)
                    this.privateCtrl.setValue("private")
                else
                    this.privateCtrl.setValue("shared")
                this.setBreadCrumbs()
            }
        })

        this.restore$.pipe(
            filter((res) => !!res),
            switchMap(() => this.restore()),
            takeUntil(this.destroy$)
        ).subscribe(() => this.save());

        this.delete$.pipe(
            filter((res) => !!res),
            switchMap(() => this.confirmDelete()),
            takeUntil(this.destroy$)
        ).subscribe(() => this.save());

        this.route$.pipe(
            filter((res) => !!res),
            switchMap((crumbs) => this.route.params.pipe(map((res) => {
                return { crumbs, res };
            }))),
            takeUntil(this.destroy$)
        ).subscribe(({ crumbs, res }) => {
            if (res.id == 'new')
                crumbs!.push({ label: "New Address"});
            else
                crumbs!.push({ label: "Loading"});
        });

        this.save$
            .pipe(
                switchMap((resourceServicePush) => resourceServicePush),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    ngAfterViewInit() {
        super.ngAfterViewInit()
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    setBreadCrumbs(removeSave: boolean = false){
        let crumbs = []
        crumbs.push({ label: 'Address Book', url:'/address-book'})
        if(this.entityForm.value.id) 
            crumbs.push({ label: "Address " + (this.entityForm.value.name.length <= 50 ? this.entityForm.value.name : this.entityForm.value.name.substring(0, 50) + '...') })
        else {
            this.route$.next(crumbs);
        }
        this.breadCrumbService.breadcrumbs.next(crumbs)
        let actions = []
        actions.push({ label: 'Cancel', routerLink: "/address-book", isPrimary: false, icon: 'close'})
        if(this.entityForm.value.id && (this.entityForm.value.deletedAt == null || this.entityForm.value.deletedAt == '')){
            actions.push({ label: 'Delete', action:this.deleteEntry.bind(this), icon: 'delete'})
        }else if(this.entityForm.value.id &&  (this.entityForm.value.deletedAt != null || this.entityForm.value.deletedAt != '')){
            actions.push({ label: 'Restore', action:this.restoreEntry.bind(this), icon: 'restore_from_trash'});
            actions.push({ label: 'Save', disabled: true, tooltip: "Address is deleted", icon: ''});
            this.breadCrumbService.actions.next(actions);
            return;
        }
        if (!this.entityForm.value.deletedAt) {
            if(this.checkFields()) {
                if (removeSave && !this.entityForm.value.id)
                    actions.push({ label: 'Save', disabled: true, tooltip:"Creating address...", icon: ''})
                else
                    actions.push({ label: 'Save', action:this.save.bind(this), isPrimary: true, icon: ''})
            }
            else
                actions.push({ label: 'Save', disabled:true, tooltip:"Please fill in all required(*) address details before saving", icon: ''})
        }
        this.breadCrumbService.actions.next(actions)
    }

    public checkFields() {
        let cond = true;
        this.entityForm.get('name')?.setValidators([validators.requiredNotEmpty]);
        this.entityForm.get('streetAddress1')?.setValidators([validators.requiredNotEmpty]);
        this.entityForm.get('city')?.setValidators([validators.requiredNotEmpty]);
        this.entityForm.get('name')?.updateValueAndValidity();
        this.entityForm.get('streetAddress1')?.updateValueAndValidity();
        this.entityForm.get('city')?.updateValueAndValidity();
        if(!this.usCatch(this.entityForm.controls["country"].value)){
            if (this.canadaCatch(this.entityForm.controls["country"].value)) {
                this.entityForm.get('postalCode')?.setValidators([Validators.required, validators.USCanadaPostalCodeValidator])
                this.entityForm.get('primaryPhoneNumber')?.setValidators([Validators.required, validators.USPhoneNumberValidator])
                this.entityForm.get('primaryFaxNumber')?.setValidators([validators.USPhoneNumberValidator])
            } else {
                this.entityForm.get('postalCode')?.setValidators([validators.requiredNotEmpty])
                this.entityForm.get('primaryPhoneNumber')?.setValidators([validators.requiredNotEmpty])
                this.entityForm.get('primaryFaxNumber')?.setValidators([]);
            }
            this.entityForm.get('state')?.setValidators([])
            this.entityForm.get('state')?.reset()
            this.entityForm.get('state')?.updateValueAndValidity()
            this.entityForm.get('postalCode')?.updateValueAndValidity()
            this.entityForm.get('primaryPhoneNumber')?.updateValueAndValidity()
            this.entityForm.get('primaryFaxNumber')?.updateValueAndValidity()
            this.entityForm.get('county')?.reset()
        }else{
            this.entityForm.get('state')?.setValidators([Validators.required])
            this.entityForm.get('postalCode')?.setValidators([Validators.required, validators.USZipCodeValidator])
            this.entityForm.get('primaryPhoneNumber')?.setValidators([Validators.required, validators.USPhoneNumberValidator])
            this.entityForm.get('primaryFaxNumber')?.setValidators([validators.USPhoneNumberValidator])
            this.entityForm.get('state')?.updateValueAndValidity()
            this.entityForm.get('postalCode')?.updateValueAndValidity()
            this.entityForm.get('primaryPhoneNumber')?.updateValueAndValidity()
            this.entityForm.get('primaryFaxNumber')?.updateValueAndValidity()
        }
        
        cond = !this.usCatch(this.entityForm.controls["country"].value) ? true : (this.entityForm.controls["state"].value != undefined ? true : false)
        if(!cond || this.accountId == "" || !this.entityForm.valid)
            return false

        if(this.privateCtrl.value == "private"){
            this.entityForm.controls['owner_uuid'].setValue(this.accountId)
            this.entityForm.controls['isPrivate'].setValue(true)
            this.entityForm.controls['isDefault'].setValue(false)
        }
        else{
            this.entityForm.controls['owner_uuid'].setValue('')
            this.entityForm.controls['isPrivate'].setValue(false)
        }
        // this.entityForm.controls['jurisdiction'].setValue(null)
        // this.entityForm.controls['county'].setValue(null)
        // this.entityForm.controls['streetAddress2'].setValue(null)
        this.entityForm.controls['corp_id'].setValue(this.corpId)
        if (this.copy.controls['corp_id']) this.copy.controls['corp_id'].setValue(this.corpId)

        return true
    }

    usCatch(val: string){
        if(!val) return false
        return (val.trim() == 'US' || val.trim() == 'USA' || val.trim() == 'United States' || val.trim() == 'United States of America')
    }

    canadaCatch(val: string){
        if(!val) return false
        return (val.trim() == 'CA' || val.trim() == 'Canada')
    }

    private trimEntity() {
        this.entity.name = this.entityForm.get('name')?.value.trim();
        this.entity.streetAddress1 = this.entityForm.get('streetAddress1')?.value.trim();
        this.entity.city = this.entityForm.get('city')?.value.trim();
        this.entity.postalCode = this.entityForm.get('postalCode')?.value.trim();
        this.entity.careOfLine = this.entityForm.get('careOfLine')?.value.trim();
        this.entity.roomNumber = this.entityForm.get('roomNumber')?.value.trim();
        this.entity.primaryPhoneNumber = this.entityForm.get('primaryPhoneNumber')?.value.trim();
        this.entity.primaryFaxNumber = this.entityForm.get('primaryFaxNumber')?.value.trim();
        this.entity.state = this.entityForm.get('state')?.value ? this.entityForm.get('state')?.value.trim() : null;
    }

    private resetForm() {
        for (const control in this.entityForm.controls) {
            this.entityForm.controls[control].setValue(this.entity[control as keyof AddressBook]);
        }
    }

    private restore() {
        return this.dialog.open(AddressRestoreDialog, {
            data: this.entity
        }).afterClosed().pipe(
            filter((res) => !!res),
            switchMap(() => {
                this.entityForm.controls['deletedAt'].setValue(null)
                this.entity.deletedAt = null;
                this.resetForm(); // Reset form to original values to avoid validation issues
                return this.entityService.push(this.entity);
            })
        );
    }

    public restoreEntry(){
        this.restore$.next(true);
    }

    private confirmDelete() {
        return this.openConfirmDeleteDialog().pipe(
            switchMap((response) => {
                if (response.confirmed) {
                    let date = new Date();
                    this.entityForm.controls["deletedAt"].setValue(date);
                    this.entity.deletedAt = date;
                    this.entity.isDeleted = true;
                    this.resetForm(); // Reset form to original values to avoid validation issues
                }
                return this.entityService.push(this.entity);
            })
        );
    }

    public deleteEntry(){
        this.delete$.next(true);
    }

    public save() {
        if(this.entity.addressType != 'COURT' && this.entity.addressType != 'ClientBranch')
            this.entityForm.get('addressType')?.setValue('ClientBranch')
        // ignore field lock html, purely ts
        let existing: string[] = this.entity.syncLocks?.split(',') || []
        let syncColumns = ['isPrivate', 'careOfLine', 'name', 'streetAddress1', 'roomNumber', 'country', 'city', 'state', 'county', 'postalCode', 'primaryPhoneNumber', 'phoneExt', 'primaryFaxNumber']

        Object.entries(this.entityForm.controls).map((prop)=>{
            if(syncColumns.findIndex((col)=> col == prop[0]) != -1
            && existing.findIndex((val)=>{return val == prop[0]}) == -1
            && prop[1].value != this.copy.controls[prop[0]]!.value
            && prop[1].touched ){
                existing?.push(prop[0])
            }
        })
        this.entityForm.get('syncLocks')?.setValue(existing.length > 0 ? existing.join(',') : '')

        Object.assign(this.entity, this.entityForm.value)
        if(!this.checkFields()){
            this.setBreadCrumbs()
            return
        }

        this.entity.deletedAt = this.entityForm.value.deletedAt
        this.entity.syncLocks = (existing.length > 0 ? existing.join(',') : '')

        this.trimEntity()

        this.save$.next(
            this.resourceService.push(this.entity).pipe(
                map((res) => {
                    this.snackbar.open("Address Saved", undefined, { duration: 2000, verticalPosition: "top", horizontalPosition: "right" });
                    this.copy.patchValue(this.entityForm.value);
                    this.setBreadCrumbs(true);
                    this.router.navigate(["/address-book/", res.id.toLocaleString().toUpperCase()]).then();
                })
            )
        );

        this.setBreadCrumbs(true)

        return 
    }
}
